import { ChevronRight } from 'lucide-react';
import { Pill } from '@/components/ui/pill';
import type { PendItem } from './types';

interface HomePendenciasProps {
  items: PendItem[];
  onItemClick: (item: PendItem) => void;
}

const DOT_COLORS: Record<PendItem['tone'], string> = {
  block: 'var(--block)',
  warn: 'var(--warn)',
};

/**
 * Card de pendências da Home mobile.
 * Exibe lista de alertas com dot colorido, label, sub-label mono e chevron.
 * Touch target mínimo 44px por row.
 * Tap em item dispara onItemClick — orquestrador decide a navegação.
 */
export function HomePendencias({ items, onItemClick }: HomePendenciasProps) {
  if (items.length === 0) return null;

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        padding: 14,
        boxShadow: 'var(--shadow-1)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <strong style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>Pendências</strong>
        <Pill tone="warn" dot>
          {items.length}
        </Pill>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onItemClick(item)}
            aria-label={`Pendência: ${item.label} — ${item.sub}. Toque para ver detalhes`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 44,
              padding: '8px 10px',
              background: 'var(--card-soft)',
              borderRadius: 'var(--r-2)',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              border: 'none',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            {/* Dot colorido */}
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: DOT_COLORS[item.tone],
                flexShrink: 0,
              }}
            />

            {/* Texto */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg)' }}>
                {item.label}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--fg-muted)',
                  marginTop: 1,
                }}
              >
                {item.sub}
              </div>
            </div>

            {/* Chevron */}
            <ChevronRight size={14} strokeWidth={1.8} aria-hidden style={{ color: 'var(--fg-dim)', flexShrink: 0 }} />
          </button>
        ))}
      </div>
    </div>
  );
}
